import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useFirebaseChat } from '../../../features/chat-support/useFirebaseChat';
import { useAdminInbox } from '../../../features/chat-support/useAdminInbox';
import { useConversationMessages } from '../../../features/chat-support/useConversationMessages';
import { MessageList } from '../../../features/chat-support/MessageList';
import { Composer } from '../../../features/chat-support/Composer';
import {
  closeConversation,
  markConversationRead,
  sendMessage,
} from '../../../features/chat-support/api';
import type { ChatConversationListItem } from '../../../features/chat-support/types';
import { cn } from '../../../utils/cn';

type StatusFilter = 'open' | 'closed' | 'all';

const firebaseEnabled = !!import.meta.env.VITE_FIREBASE_DATABASE_URL;

export default function ChatSupportPage() {
  const user = useAuthStore((s) => s.user);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fb = useFirebaseChat(firebaseEnabled);
  const { items, loading, patchStatus, refetch } = useAdminInbox({
    db: fb.db,
    params: { status: statusFilter, q: search },
    enabled: true,
  });

  const selected = useMemo<ChatConversationListItem | null>(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  const { messages, loadOlder, hasMore, loadingOlder } = useConversationMessages({
    db: fb.db,
    conversationId: selectedId,
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!selectedId) return;
    markConversationRead(selectedId).catch(() => {});
  }, [selectedId, messages.length]);

  const onSend = async (body: string) => {
    if (!selectedId) return;
    await sendMessage({ conversationId: selectedId, body });
  };

  const onClose = async () => {
    if (!selectedId) return;
    const id = selectedId;
    patchStatus(id, 'closed');
    setSelectedId(null);
    try {
      await closeConversation(id);
      // Pull the authoritative list back from Postgres so the closed row
      // disappears even if RTDB hasn't caught up yet.
      await refetch();
    } catch (err) {
      patchStatus(id, 'open');
      throw err;
    }
  };

  return (
    <div
      className="flex h-[calc(100vh-4rem)] bg-white"
      data-test-id="admin-chat-support"
    >
      <aside className="flex w-80 flex-col border-r border-gray-200">
        <div className="p-3 space-y-2 border-b border-gray-200">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search user or company…"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-test-id="chat-inbox-search"
          />
          <div className="flex gap-1 text-xs">
            {(['open', 'closed', 'all'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={cn(
                  'rounded-full px-3 py-1 capitalize',
                  statusFilter === s ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700',
                )}
                data-test-id={`chat-inbox-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-3 text-xs text-gray-500">Loading…</div>}
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setSelectedId(it.id)}
              className={cn(
                'w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-gray-50',
                selectedId === it.id && 'bg-orange-50',
              )}
              data-test-id={`chat-inbox-row-${it.id}`}
            >
              <div className="flex justify-between items-baseline">
                <div className="font-medium text-sm truncate">
                  {it.user_name || it.user_email}
                </div>
                {it.unread_for_admin > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full bg-orange-500 text-[10px] text-white">
                    {it.unread_for_admin}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 truncate">{it.company_name}</div>
              <div className="text-xs text-gray-600 truncate mt-1">
                {it.last_message_preview}
              </div>
            </button>
          ))}
          {items.length === 0 && !loading && (
            <div className="p-6 text-center text-sm text-gray-500">No conversations yet.</div>
          )}
        </div>
      </aside>

      <section className="flex flex-1 flex-col">
        {!firebaseEnabled && (
          <div className="bg-yellow-50 text-yellow-800 text-xs px-4 py-2 border-b border-yellow-200">
            Firebase is not configured. Live updates are disabled until VITE_FIREBASE_* env vars
            are set.
          </div>
        )}
        {selected ? (
          <>
            <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <div className="font-semibold text-sm">
                  {selected.user_name || selected.user_email}
                </div>
                <div className="text-xs text-gray-500">{selected.company_name}</div>
              </div>
              {selected.status === 'open' && (
                <button
                  onClick={onClose}
                  className="text-xs rounded-md border border-gray-200 px-3 py-1.5 hover:bg-gray-50"
                  data-test-id="chat-close-button"
                >
                  Close conversation
                </button>
              )}
            </header>
            <MessageList
              messages={messages}
              selfUserId={user?.id ?? ''}
              onLoadOlder={loadOlder}
              hasMore={hasMore}
              loadingOlder={loadingOlder}
            />
            <Composer onSend={onSend} disabled={!fb.ready} />
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            Select a conversation to start chatting.
          </div>
        )}
      </section>
    </div>
  );
}
